import React, { useEffect, useState } from "react";
import { Table, Input, Tag, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { UserManagerWrapper, ToolbarRow, TableContainer } from "./styled";
import { useUserAdmin } from "../../hooks/useUserAdmin";
import { getUserActivity } from "../../api/monitoring";
import { showConfirm } from "../../helpers/modal-confirm";
import ModalNormal from "../../components/common/modal";
import EditUser from "../../components/UserTable/EditUser/EditUser";
import editTable from "../../assets/icon/edit.svg";
import trashTable from "../../assets/icon/trash.svg";

const { Search } = Input;

function formatTime(isoString) {
  if (!isoString) return "-";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const UserManager = ({ modalData, setModalData }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [activityData, setActivityData] = useState({});
  const [activityLoading, setActivityLoading] = useState(true);

  const closeModal = (open) => {
    setModalData(open);
  };

  const { userList, createUser, updateUser, deleteUser } =
    useUserAdmin(closeModal);

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const isAdmin = user?.is_superuser === "True";

  useEffect(() => {
    if (isAdmin) {
      const fetchActivity = async () => {
        try {
          const res = await getUserActivity();
          const map = {};
          (res?.users || []).forEach((u) => {
            map[u.username] = u;
          });
          setActivityData(map);
        } catch (e) {
          // silently fail for non-admin
        } finally {
          setActivityLoading(false);
        }
      };
      fetchActivity();
    } else {
      setActivityLoading(false);
    }
  }, [isAdmin]);

  const mergedUsers = userList.map((u) => {
    const activity = activityData[u.username] || {};
    return {
      ...u,
      last_login: activity.last_login || null,
      login_count_30d: activity.login_count_30d || 0,
      last_updated: activity.last_updated || null,
    };
  });

  const filtered = mergedUsers.filter(
    (u) =>
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.province_name?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: t("userManagement.user"),
      dataIndex: "username",
      key: "username",
      width: 120,
      ellipsis: true,
      sorter: (a, b) => (a.username || "").localeCompare(b.username || ""),
    },
    {
      title: t("userManagement.email"),
      dataIndex: "email",
      key: "email",
      width: 160,
      ellipsis: true,
    },
    {
      title: t("userManagement.city"),
      dataIndex: "province_name",
      key: "province_name",
      width: 120,
      ellipsis: true,
    },
    {
      title: t("userManagement.permission_sort"),
      dataIndex: "is_superuser",
      key: "is_superuser",
      width: 90,
      render: (val) =>
        val ? (
          <Tag color="red">Admin</Tag>
        ) : (
          <Tag color="blue">User</Tag>
        ),
    },
    ...(isAdmin
      ? [
          {
            title: t("userManagement.lastActive") || "Last Active",
            dataIndex: "last_login",
            key: "last_login",
            width: 120,
            render: (val) => formatTime(val),
            sorter: (a, b) =>
              new Date(a.last_login || 0) - new Date(b.last_login || 0),
            defaultSortOrder: "descend",
          },
          {
            title: t("userManagement.logins30d") || "Logins (30d)",
            dataIndex: "login_count_30d",
            key: "login_count_30d",
            width: 110,
            sorter: (a, b) => a.login_count_30d - b.login_count_30d,
          },
          {
            title: t("userManagement.lastUpdated") || "Last Updated",
            dataIndex: "last_updated",
            key: "last_updated",
            width: 120,
            render: (val) => formatTime(val),
            sorter: (a, b) =>
              new Date(a.last_updated || 0) - new Date(b.last_updated || 0),
          },
        ]
      : []),
    {
      title: t("userManagement.dateCreated"),
      dataIndex: "date_joined",
      key: "date_joined",
      width: 110,
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
    {
      title: t("userManagement.action") || "Action",
      key: "action",
      width: 80,
      render: (_, item) => (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <img
            src={editTable}
            alt="edit"
            style={{ cursor: "pointer" }}
            onClick={() =>
              setModalData({
                ...item,
                isEdit: true,
              })
            }
          />
          <img
            src={trashTable}
            alt="delete"
            style={{ cursor: "pointer" }}
            onClick={() => {
              showConfirm({
                title: t("userManagement.confirmDelete"),
                onOk: () => {
                  deleteUser({ user_id: String(item?.id) });
                },
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <UserManagerWrapper>
      <ToolbarRow>
        <Search
          placeholder={t("userManagement.searchPlaceholder")}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalData({ isEdit: false })}
        >
          {t("userManagement.addUser")}
        </Button>
      </ToolbarRow>

      <TableContainer>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          size="small"
          loading={activityLoading}
          pagination={{
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ["10", "15", "30", "50"],
          }}
        />
      </TableContainer>

      <ModalNormal
        visible={!!modalData}
        setVisible={closeModal}
        width={500}
        title={
          modalData?.isEdit
            ? t("userManagement.editUser")
            : t("userManagement.createUser")
        }
        onCancel={() => setModalData(false)}
        beforelose={() => setModalData(false)}
      >
        <EditUser
          modalData={modalData}
          setIsOpen={closeModal}
          createUser={createUser}
          updateUser={updateUser}
        />
      </ModalNormal>
    </UserManagerWrapper>
  );
};

export default UserManager;
